import {
  SelectProvider,
  Select,
  SelectPopover,
  SelectItem,
  SelectValue,
  useSelectStore,
  useStoreState,
} from '@ariakit/react'
import { ChevronDown } from 'lucide-react'

export interface FilterOption {
  value: string
  label: string
}

export interface FilterSelectProps {
  value: string
  onValueChange: (val: string) => void
  options: FilterOption[]
  disabled?: boolean
  placeholder?: string
  id?: string
  'aria-labelledby'?: string
  icon?: React.ReactNode
  className?: string
}

const btnBase =
  'flex h-10 w-full items-center justify-between rounded-[var(--radius)] border border-[var(--color-input)] bg-[var(--color-background)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] disabled:cursor-not-allowed disabled:opacity-50'
const itemBase =
  'flex cursor-pointer items-center px-4 py-2 text-sm text-[var(--color-foreground)] data-[active-item]:bg-[color-mix(in_oklab,var(--color-accent)_12%,transparent)]'
const itemSelected = 'text-[var(--color-accent)] font-medium'

function SelectChevron() {
  const store = useSelectStore()
  const open = useStoreState(store, 'open')
  return (
    <ChevronDown
      className={`h-4 w-4 shrink-0 text-[var(--color-muted-foreground)] transition-transform ${open ? 'rotate-180' : ''}`}
      aria-hidden
    />
  )
}

export function FilterSelect({
  value,
  onValueChange,
  options,
  disabled = false,
  placeholder = 'Select option',
  id,
  'aria-labelledby': ariaLabelledBy,
  icon,
  className,
}: FilterSelectProps) {
  const selectedOption = options.find((opt) => opt.value === value)

  return (
    <div className="relative w-full">
      <SelectProvider value={value} setValue={onValueChange}>
        <Select
          id={id}
          aria-labelledby={ariaLabelledBy}
          disabled={disabled}
          className={`${btnBase} ${className || ''}`.trim()}
        >
          <span className="flex flex-1 items-center gap-2">
            {icon}
            <span
              className={
                selectedOption
                  ? 'text-[var(--color-foreground)]'
                  : 'text-[var(--color-muted-foreground)]'
              }
            >
              <SelectValue fallback={placeholder}>
                {(val) => {
                  const str = Array.isArray(val) ? val[0] : val
                  return options.find((o) => o.value === str)?.label ?? placeholder
                }}
              </SelectValue>
            </span>
          </span>
          <SelectChevron />
        </Select>
        <input type="hidden" name={id} value={value} />
        <SelectPopover
          gutter={4}
          sameWidth
          className="z-[9999] mt-1 max-h-72 overflow-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-lg"
        >
          {options.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              className={`${itemBase} ${opt.value === value ? itemSelected : ''}`.trim()}
              focusOnHover
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectPopover>
      </SelectProvider>
    </div>
  )
}
